/**
 * What a heritage is made of, and where every piece of it came from.
 *
 * The Greek material exists in three layers, and they are not the same kind of thing:
 *
 *   Photographs  Emmett Brogan stood in these places and took these pictures. They are
 *                his work and his copyright. Nothing here is ours.
 *   Rooms        Gaussian splats generated *from* those photographs. Nobody scanned
 *                the Acropolis with a rig; a model reconstructed it from pictures.
 *   Objects      Meshy turned single photographs of single objects into geometry. Same
 *                story, smaller subject.
 *
 * Which means only one honest sentence covers the whole section: the photographs are
 * Emmett's, and everything three-dimensional was generated from them. The `origin`
 * field carries that distinction into the markup so the page cannot accidentally
 * describe a generation as a capture — the mistake this project has already made once,
 * with the Saudi scans, and which is much worse when the source is a named friend.
 *
 * Titles describe only what is visible in the frame. Where a museum interior gives no
 * reliable clue to the city, `place` is left empty rather than guessed: an attribution
 * that is confidently wrong is worse than one that is quiet.
 */

import type { RegionId } from "./regions";

export type Credit = {
  name: string;
  /** Where the work actually lives, so credit is a link and not just a spelling. */
  url: string;
};

/**
 * The photographer. One constant, referenced everywhere, so the credit cannot appear
 * on one surface and be forgotten on another — and so correcting a spelling is one
 * edit rather than a search.
 */
export const EMMETT: Credit = {
  name: "Emmett Brogan",
  url: "https://www.instagram.com/emmettb94/",
};

/**
 * How a thing came to exist. This decides what the page is allowed to claim, so it is
 * a field rather than a convention.
 */
export type Origin = "photograph" | "generated";

export type Photograph = {
  slug: string;
  /** Published name under `public/collection/`, not the original camera filename. */
  file: string;
  title: string;
  /** Only where the subject makes it certain. Empty is an honest answer. */
  place: string;
  /**
   * A line or two about what is in the frame.
   *
   * English only, and deliberately: `title` and `place` are not translated either.
   * These are statements of fact about specific monuments, and a machine-shaped
   * paraphrase of an archaeological date in four more languages would be a liability
   * rather than a courtesy. Translating them properly is a job for a person.
   *
   * The rule for writing one: only what is established. Where the photograph does not
   * settle a question — which museum, which animal, whose hand — the story says so
   * instead of filling the gap.
   */
  story: string;
  region: RegionId;
  credit: Credit;
};

export type SolidObject = {
  slug: string;
  file: string;
  title: string;
  /** The photograph this geometry was generated from. */
  from: string;
  /** What the thing is, for a caption that reads as a label rather than a filename. */
  story: string;
  region: RegionId;
  origin: Origin;
  credit: Credit;
};

const GREECE: RegionId = "greece";

export const PHOTOGRAPHS: readonly Photograph[] = [
  { slug: "parthenon", file: "parthenon.webp", title: "The Parthenon", place: "Athens", story: "Built for Athena between 447 and 432 BC, in the years Athens was at its richest and least modest. It has since been a church and a mosque, and lost its roof in 1687 when a Venetian shell found the gunpowder the Ottoman garrison was storing inside. The scaffolding has been there, in one form or another, for forty years.", region: GREECE, credit: EMMETT },
  { slug: "erechtheion-caryatids", file: "erechtheion-caryatids.webp", title: "The caryatid porch of the Erechtheion", place: "Athens", story: "Six women carry the porch roof instead of columns, carved in the 420s BC. These are casts. Five of the originals stand in the Acropolis Museum a few hundred metres away, out of the weather; the sixth has been in London since Lord Elgin took it.", region: GREECE, credit: EMMETT },
  { slug: "odeon-of-herodes-atticus", file: "odeon-of-herodes-atticus.webp", title: "The Odeon of Herodes Atticus", place: "Athens", story: "A Roman theatre of AD 161, paid for by Herodes Atticus as a memorial to his wife Regilla. It seats about five thousand, and still does — the Athens Festival plays here every summer, which makes it one of the oldest rooms on this page that is still in use for the thing it was built for.", region: GREECE, credit: EMMETT },
  { slug: "temple-of-hephaestus", file: "temple-of-hephaestus.webp", title: "The Temple of Hephaestus, above the Agora", place: "Athens", story: "The best-preserved Greek temple anywhere, and it survived by being useful: it stood as a church for something like fourteen centuries, which is why it still has its roof and its neighbours in the Agora do not.", region: GREECE, credit: EMMETT },
  { slug: "panathenaic-stadium", file: "panathenaic-stadium.webp", title: "The Panathenaic Stadium", place: "Athens", story: "Cut into the hillside in the fourth century BC, rebuilt entirely in white Pentelic marble for the first modern Olympics in 1896. It is the only stadium in the world built from marble, and the tight horseshoe is the ancient running track's shape rather than a designer's.", region: GREECE, credit: EMMETT },
  { slug: "academy-of-athens", file: "academy-of-athens.webp", title: "The Academy of Athens", place: "Athens", story: "Nineteenth century, not ancient — Theophil Hansen's building, and the most ornate third of the neoclassical trilogy on Panepistimiou. Athena and Apollo stand on the columns out front. It is a very careful nineteenth-century argument about what the fifth century BC looked like.", region: GREECE, credit: EMMETT },
  { slug: "national-garden-sundial", file: "national-garden-sundial.webp", title: "A sundial in the National Garden", place: "Athens", story: "The garden was laid out in the 1840s as the private grounds of the palace that is now Parliament, and opened to the city afterwards. It is the shaded route through the middle of Athens, and the sundial keeps working whatever else is going on.", region: GREECE, credit: EMMETT },
  { slug: "athens-moonrise", file: "athens-moonrise.webp", title: "Moonrise over Lycabettus", place: "Athens", story: "Lycabettus, the limestone hill that stands above everything else in Athens, with the city running out to the dark underneath it.", region: GREECE, credit: EMMETT },
  { slug: "athens-sunset", file: "athens-sunset.webp", title: "Sunset over the city", place: "Athens", story: "The rooftops going orange, and the ridge of mountains that closes the basin on every side except the sea. This frame is also the still that opens the second scene at the top of this page.", region: GREECE, credit: EMMETT },
  { slug: "rotunda-of-galerius", file: "rotunda-of-galerius.webp", title: "The Rotunda of Galerius", place: "Thessaloniki", story: "Built around AD 306, probably for the emperor Galerius and probably as his mausoleum, though he was never buried in it. It has been a church and a mosque since — the minaret beside it is the only one left standing in the city — and it is now part of a UNESCO listing.", region: GREECE, credit: EMMETT },
  { slug: "white-tower", file: "white-tower.webp", title: "The White Tower, from the waterfront", place: "Thessaloniki", story: "The tower on the seafront is Ottoman, put up in the sixteenth century on the corner of a Byzantine wall, and it spent long enough as a prison to be called the Tower of Blood before it was whitewashed and renamed. The whitewash is gone; the name stayed.", region: GREECE, credit: EMMETT },
  { slug: "thessaloniki-mural", file: "thessaloniki-mural.webp", title: "A mural on a gable wall", place: "Thessaloniki", story: "Painted on a gable end left exposed by the building that used to stand beside it. We do not know who painted it. The photograph is Emmett's; the artwork in it belongs to someone we cannot name, and naming them here would need a source we do not have.", region: GREECE, credit: EMMETT },
  // Museum interiors. The objects are unmistakable; the city they are standing in is
  // not, and a wrong museum is a wrong fact, so `place` stays empty.
  { slug: "athena-bronze", file: "athena-bronze.webp", title: "Athena, in bronze", place: "", story: "Helmeted, one hand open and the other once holding a spear. Large bronzes almost never survive antiquity — they were too valuable not to melt down — so the ones that do are usually the ones that were lost at sea or buried in a hurry.", region: GREECE, credit: EMMETT },
  { slug: "good-shepherd", file: "good-shepherd.webp", title: "The Good Shepherd", place: "", story: "A youth carrying a ram across his shoulders: the earliest Christian art borrowed the figure wholesale from Greek and Roman sculpture, where it had been a pastoral type for centuries before it meant anything else.", region: GREECE, credit: EMMETT },
  { slug: "grave-stele", file: "grave-stele.webp", title: "A grave stele, inscribed ΔΟΡΚΑΣ", place: "", story: "The name cut into it is ΔΟΡΚΑΣ — Dorkas. Attic grave reliefs like this one are usually a quiet domestic scene rather than a heroic one, and the person being mourned is often the one seated.", region: GREECE, credit: EMMETT },
  { slug: "red-figure-krater", file: "red-figure-krater.webp", title: "A red-figure krater", place: "", story: "A krater is a mixing bowl: wine went in, water went in after it, and drinking neat wine was what other people did. Red-figure painting — leaving the figures in the clay's own colour and blacking in around them — came in at Athens around 530 BC.", region: GREECE, credit: EMMETT },
  // Species deliberately unnamed. It reads as a big cat and the generator called it a
  // calf; neither is evidence, and a museum label is not the place to guess.
  { slug: "terracotta-feline", file: "terracotta-feline.webp", title: "A terracotta animal", place: "", story: "Small, cracked across the middle, and sitting up. We are not going to name the species: it reads as a big cat, the model that generated the object beside it called it a calf, and neither of those is evidence.", region: GREECE, credit: EMMETT },
];

/**
 * Geometry generated from a single photograph. Both of these have their source in the
 * list above, which is the whole point of showing them: the pair is the argument.
 */
export const OBJECTS: readonly SolidObject[] = [
  {
    slug: "athena-vigil",
    file: "athena-vigil.glb",
    title: "Athena",
    from: "athena-bronze",
    story: "Generated from the single photograph beside it — one frame, one angle, and a model guessing at everything the camera could not see. The back of the figure is invention.",
    region: GREECE,
    origin: "generated",
    credit: EMMETT,
  },
  {
    slug: "marble-shepherd",
    file: "marble-shepherd.glb",
    title: "The Good Shepherd",
    from: "good-shepherd",
    story: "The drapery and the ram survive the translation; the face does not really. Look at where the two disagree, because that is where the generation stops being a record and starts being a guess.",
    region: GREECE,
    origin: "generated",
    credit: EMMETT,
  },
  {
    slug: "stone-embrace",
    file: "stone-embrace.glb",
    title: "A grave stele",
    from: "grave-stele",
    story: "A relief is a hard thing to generate from one photograph: it is almost flat, so the model has to decide how deep every carved edge goes, and there is nothing in the picture to tell it.",
    region: GREECE,
    origin: "generated",
    credit: EMMETT,
  },
  {
    slug: "muse-vase",
    file: "muse-vase.glb",
    title: "A red-figure krater",
    from: "red-figure-krater",
    story: "A vessel is the case where this works best. It is close to a solid of revolution, so a single elevation carries most of the information — the painted figures wrap around a shape the model can infer.",
    region: GREECE,
    origin: "generated",
    credit: EMMETT,
  },
  {
    slug: "clay-calf",
    file: "clay-calf.glb",
    title: "A terracotta animal",
    from: "terracotta-feline",
    story: "The name came from the generator, which decided it was a calf. The photograph beside it does not settle the question, so the label on the photograph does not pretend to.",
    region: GREECE,
    origin: "generated",
    credit: EMMETT,
  },
];

export function photographsIn(region: RegionId): readonly Photograph[] {
  return PHOTOGRAPHS.filter((photograph) => photograph.region === region);
}

export function objectsIn(region: RegionId): readonly SolidObject[] {
  return OBJECTS.filter((object) => object.region === region);
}

export function photographFor(slug: string): Photograph | null {
  return PHOTOGRAPHS.find((photograph) => photograph.slug === slug) ?? null;
}

export function objectFor(slug: string | null): SolidObject | null {
  return OBJECTS.find((object) => object.slug === slug) ?? null;
}

/** Every distinct person to credit for a region, in one pass, without duplicates. */
export function creditsFor(region: RegionId): readonly Credit[] {
  const seen = new Map<string, Credit>();
  for (const photograph of photographsIn(region)) seen.set(photograph.credit.url, photograph.credit);
  for (const object of objectsIn(region)) seen.set(object.credit.url, object.credit);
  return [...seen.values()];
}
